/**
 * Paso Audiencia del asistente: el bloque «Excluir» (28/10/2026), debajo de «Por compras». De la gente elegida en la
 * pestaña (el Público de Activa, un público de la Base o, desde el 29/10/2026, un análisis de Activa), sacar a quien ya
 * recibió otras campañas del oficial y a quien está en una lista de exclusión de la marca («Empleados», «VIP que no se
 * tocan»). Las reglas son del backend (`util/exclusiones.util.ts`): «recibió» es que le llegó, y la persona es la misma
 * por su id o por su celular.
 *
 * - Arriba, siempre a la vista (también plegado), las listas que valen para todas las campañas (29/10/2026): se aplican
 *   solas, no se eligen; con cuántos saca cada una cuando el conteo lo dice, y el link a la página de las listas para
 *   cambiarlo. Abajo, lo que se elige para esta campaña: las campañas recibidas y las listas que no valen siempre.
 *
 * - Plegado por defecto. Con algo elegido, la cabecera dice qué, y abre solo si la campaña ya lo tenía.
 * - Las campañas: las que le llegaron a alguien, con su fecha y a cuántos; con muchas, se buscan por nombre.
 * - Las listas: las de la marca, con cuántos celulares tienen; se arman en su página (`RUTA_LISTAS_EXCLUSION`, se abre
 *   en otra pestaña para no perder lo elegido acá) y «Actualizar» las vuelve a traer.
 * - Una lista elegida que se borró lo dice y ofrece sacarla: el asistente no deja seguir con ella (el chequeo frenaría).
 * - Cuántos saca cada cosa y cuántos quedan: lo dice el conteo de la pestaña (`resumen`), el mismo del chequeo.
 */

import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, ExternalLink, Loader2, RefreshCw, Search, UserX, X } from "lucide-react";
import type { AudienciaResumen, CampanaParaExcluir, ExcluirAudiencia, ListaExclusion, ResumenExclusiones } from "../types";
import {
  CAMPANAS_PARA_BUSCAR,
  EXCLUIR_VACIO,
  RUTA_LISTAS_EXCLUSION,
  buscarCampanas,
  describirExclusiones,
  diaDeCampana,
  excluirActivo,
  listasBorradas,
  listasParaElegir,
  listasSiempre,
  textoContenido,
  textoQuedanExclusiones,
  textoSaca,
} from "../logic/exclusiones";
import { num } from "../../campana-nueva/utils/format";

interface Props {
  excluir: ExcluirAudiencia;
  onExcluir: (e: ExcluirAudiencia) => void;
  /** las campañas que le llegaron a alguien (`GET /exclusiones/campanas`): null mientras llegan */
  campanas: CampanaParaExcluir[] | null;
  /** las listas de exclusión de la marca (`GET /exclusiones/listas`), las que valen siempre y las demás: null mientras llegan */
  listas: ListaExclusion[] | null;
  errorOpciones?: string;
  onReintentar?: () => void;
  /** lo que la campaña tenía guardado, con los nombres (una lista borrada sigue acá, marcada) */
  guardadas?: AudienciaResumen["excluir"];
  /** lo que dijo el conteo de la pestaña: null sin exclusiones (o sin conteo todavía) */
  resumen: ResumenExclusiones | null;
  contando: boolean;
  /** esta campaña: no se ofrece excluirse a sí misma */
  campanaId?: string;
}

export function ExcluirBloque({ excluir, onExcluir, campanas, listas, errorOpciones = "", onReintentar, guardadas, resumen, contando, campanaId = "" }: Props) {
  const idPanel = useId();
  const idBuscar = useId();
  const activo = excluirActivo(excluir);
  // Plegado, salvo que la campaña venga con exclusiones; hasta que se lo toca (null) sigue a lo elegido, que puede llegar
  // después de que el bloque ya se mostró (como «Por compras»).
  const [abiertoElegido, setAbierto] = useState<boolean | null>(null);
  const abierto = abiertoElegido ?? activo;
  const [busqueda, setBusqueda] = useState("");
  const cambiar = (e: ExcluirAudiencia) => {
    if (abiertoElegido === null) setAbierto(true);
    onExcluir(e);
  };
  const alternar = (clave: "campanas" | "listas", id: string, marcada: boolean) =>
    cambiar({ ...excluir, [clave]: marcada ? [...excluir[clave].filter((x) => x !== id), id] : excluir[clave].filter((x) => x !== id) });

  const ofrecidas = (campanas || []).filter((c) => c.id !== campanaId);
  const visibles = buscarCampanas(ofrecidas, busqueda);
  const borradas = listasBorradas(excluir, guardadas, listas);
  // los nombres de lo elegido: de las opciones y, si no están (una borrada), de lo guardado
  const nombres = new Map<string, string>([
    ...(guardadas?.campanas || []).map((c) => [c.id, c.nombre] as [string, string]),
    ...(guardadas?.listas || []).map((l) => [l.id, l.nombre] as [string, string]),
    ...ofrecidas.map((c) => [c.id, c.nombre] as [string, string]),
    ...(listas || []).map((l) => [l.id, l.nombre] as [string, string]),
  ]);
  const saca = (clave: "campanas" | "listas", id: string): number | null => resumen?.[clave].find((x) => x.id === id)?.personas ?? null;
  // una campaña elegida que no está entre las ofrecidas (más vieja que las que se listan): igual se muestra, marcada
  const elegidasFuera = excluir.campanas.filter((id) => !ofrecidas.some((c) => c.id === id));
  // las que valen para todas las campañas se aplican solas (arriba); se eligen sólo las demás
  const siempre = listasSiempre(listas);
  const elegibles = listasParaElegir(listas);
  const hayAlgo = activo || siempre.length > 0;

  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid="excluir">
      <button type="button" aria-expanded={abierto} aria-controls={idPanel} onClick={() => setAbierto(!abierto)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex flex-wrap items-center gap-2">
          <UserX size={16} className="text-violet-600" />
          <span className="text-sm font-semibold text-slate-900">Excluir</span>
          {activo
            ? <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700">Excluyendo</span>
            : <span className="text-xs text-slate-500">opcional: a quien ya recibió otra campaña o está en una lista</span>}
          {borradas.length > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">Revisar</span>}
        </span>
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${abierto ? "rotate-180" : ""}`} />
      </button>
      {siempre.length > 0 && (
        <div className="space-y-0.5 px-4 pb-3 text-xs text-slate-700" data-testid="excluir-siempre">
          <p>
            <span className="font-semibold">Siempre se saca a:</span>{" "}
            {siempre.map((l, i) => {
              const n = saca("listas", l.id);
              return (
                <span key={l.id}>
                  {i > 0 && " · "}
                  <span className="font-medium text-slate-900">{l.nombre}</span>{" "}
                  <span className="text-slate-500">({n !== null ? textoSaca(n) : textoContenido(l)})</span>
                </span>
              );
            })}
          </p>
          <p className="text-slate-500">
            {siempre.length === 1 ? "Vale" : "Valen"} para todas las campañas de la marca, sin elegirlas.{" "}
            <Link to={RUTA_LISTAS_EXCLUSION} target="_blank" rel="noopener" className="font-semibold text-violet-700 hover:underline">Cambiarlo en las listas</Link>
          </p>
        </div>
      )}
      {!abierto && activo && (
        <p className="px-4 pb-3 text-xs text-slate-600" data-testid="excluir-resumen">{describirExclusiones(excluir, nombres).join(" · ")}</p>
      )}

      {abierto && (
        <div id={idPanel} className="space-y-4 border-t border-slate-100 px-4 py-3 text-sm text-slate-700">
          {errorOpciones && (
            <p className="flex items-center justify-between gap-3 text-red-700" role="alert">
              <span>No pudimos traer las campañas y las listas. {errorOpciones}</span>
              {onReintentar && <button type="button" onClick={onReintentar} className="font-semibold underline">Reintentar</button>}
            </p>
          )}

          {borradas.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2 text-amber-900" role="alert" data-testid="excluir-lista-borrada">
              <p className="flex items-start gap-2">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <span>La lista {b.nombre ? `«${b.nombre}»` : "de exclusión que elegiste"} se borró: la campaña no puede seguir con ella.</span>
              </p>
              <button type="button" onClick={() => cambiar({ ...excluir, listas: excluir.listas.filter((x) => x !== b.id) })}
                className="inline-flex items-center gap-1 font-semibold underline">
                <X size={13} /> Sacarla
              </button>
            </div>
          ))}

          <fieldset>
            <legend className="mb-1 text-xs font-semibold text-slate-600">Que no la reciba quien ya recibió</legend>
            {campanas === null && !errorOpciones ? (
              <p className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Buscando las campañas…</p>
            ) : campanas !== null && ofrecidas.length === 0 ? (
              <p className="text-xs text-slate-500">Todavía no hay campañas que le hayan llegado a alguien.</p>
            ) : campanas !== null ? (
              <>
                {ofrecidas.length > CAMPANAS_PARA_BUSCAR && (
                  <label htmlFor={idBuscar} className="mb-2 flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1">
                    <Search size={14} className="text-slate-400" />
                    <span className="sr-only">Buscar una campaña</span>
                    <input id={idBuscar} type="search" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar una campaña"
                      className="w-full bg-transparent text-sm focus:outline-none" />
                  </label>
                )}
                <ul className="max-h-48 space-y-1.5 overflow-y-auto">
                  {visibles.map((c) => {
                    const n = excluir.campanas.includes(c.id) ? saca("campanas", c.id) : null;
                    return (
                      <li key={c.id}>
                        <label className="inline-flex flex-wrap items-center gap-x-1.5">
                          <input type="checkbox" className="accent-violet-600" checked={excluir.campanas.includes(c.id)} onChange={(e) => alternar("campanas", c.id, e.target.checked)} />
                          <span className="font-medium text-slate-800">{c.nombre}</span>
                          <span className="text-xs text-slate-500">
                            {[diaDeCampana(c.fecha), `le llegó a ${num(c.recibieron)}`].filter(Boolean).join(" · ")}
                          </span>
                          {n !== null && <span className="text-xs font-semibold text-violet-700">· {textoSaca(n)}</span>}
                        </label>
                      </li>
                    );
                  })}
                  {!visibles.length && <li className="text-xs text-slate-500">Ninguna campaña se llama así.</li>}
                  {elegidasFuera.map((id) => (
                    <li key={id}>
                      <label className="inline-flex items-center gap-1.5">
                        <input type="checkbox" className="accent-violet-600" checked onChange={() => alternar("campanas", id, false)} />
                        <span className="font-medium text-slate-800">{nombres.get(id) || "Una campaña que ya no está"}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </fieldset>

          <fieldset>
            <legend className="mb-1 text-xs font-semibold text-slate-600">{siempre.length ? "Otras listas, sólo para esta campaña" : "Listas de exclusión"}</legend>
            {listas === null && !errorOpciones ? (
              <p className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Buscando las listas…</p>
            ) : listas !== null && listas.length === 0 ? (
              <p className="text-xs text-slate-500">Todavía no hay listas: armá una (por ejemplo, «Empleados»). Vale para todas las campañas, o la elegís acá.</p>
            ) : listas !== null && elegibles.length === 0 ? (
              <p className="text-xs text-slate-500">Las listas de la marca valen para todas las campañas: ya se sacan arriba. Una que se elija por campaña aparece acá.</p>
            ) : listas !== null ? (
              <ul className="space-y-1.5">
                {elegibles.map((l) => {
                  const n = excluir.listas.includes(l.id) ? saca("listas", l.id) : null;
                  return (
                    <li key={l.id}>
                      <label className="inline-flex flex-wrap items-center gap-x-1.5">
                        <input type="checkbox" className="accent-violet-600" checked={excluir.listas.includes(l.id)} onChange={(e) => alternar("listas", l.id, e.target.checked)} />
                        <span className="font-medium text-slate-800">{l.nombre}</span>
                        <span className="text-xs text-slate-500">{textoContenido(l)}</span>
                        {n !== null && <span className="text-xs font-semibold text-violet-700">· {textoSaca(n)}</span>}
                      </label>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
              <Link to={RUTA_LISTAS_EXCLUSION} target="_blank" rel="noopener" className="inline-flex items-center gap-1 font-semibold text-violet-700 hover:underline">
                <ExternalLink size={12} /> Ver y editar las listas
              </Link>
              {onReintentar && listas !== null && (
                <button type="button" onClick={onReintentar} className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:underline">
                  <RefreshCw size={12} /> Actualizar
                </button>
              )}
            </div>
          </fieldset>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2" aria-live="polite" data-testid="excluir-quedan">
            {!hayAlgo ? (
              <p className="text-slate-500">Sin exclusiones: no se saca a nadie.</p>
            ) : contando || !resumen ? (
              <p className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Contando…</p>
            ) : (
              <p>
                <strong>{textoQuedanExclusiones(resumen)}</strong>
                {resumen.enListas > 0 && resumen.porCampanas > 0 && (
                  <span className="block text-xs text-slate-500">
                    {num(resumen.enListas)} por estar en una lista y {num(resumen.porCampanas)} por haber recibido otra campaña: quien está en una lista y además la recibió, cuenta en la lista.
                  </span>
                )}
              </p>
            )}
            {activo && (
              <button type="button" onClick={() => cambiar({ ...EXCLUIR_VACIO })} className="shrink-0 text-xs font-semibold text-violet-700 hover:underline">
                {siempre.length ? "Sacar lo elegido para esta campaña" : "Sacar las exclusiones"}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default ExcluirBloque;
